import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useDropdownDismiss } from "../../../../hooks/useDropdownDismiss";
import { KebabButton } from "../../../../components/KebabButton";
import { Menu, MenuItem, MenuWrapper } from "./styles";
import { RecurringCardMenuProps } from "./types";

export const RecurringCardMenu: React.FC<RecurringCardMenuProps> = ({
  onEdit,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; right: number }>({
    top: 0,
    right: 0,
  });
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  useDropdownDismiss(anchorRef, menuRef, isOpen, close);

  const updatePosition = useCallback(() => {
    if (!anchorRef.current) {
      return;
    }
    const rect = anchorRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) {
        updatePosition();
      }
      return !prev;
    });
  }, [updatePosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handle = () => close();
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [isOpen, close]);

  const handleEdit = useCallback(() => {
    close();
    onEdit();
  }, [close, onEdit]);

  const handleDelete = useCallback(() => {
    close();
    onDelete();
  }, [close, onDelete]);

  return (
    <MenuWrapper>
      <KebabButton
        size="small"
        ref={anchorRef}
        onClick={toggle}
        aria-label="Recurring transaction options"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      />
      {isOpen &&
        createPortal(
          <Menu
            ref={menuRef}
            role="menu"
            $top={position.top}
            $right={position.right}
          >
            <MenuItem type="button" role="menuitem" onClick={handleEdit}>
              <EditOutlined />
              Edit
            </MenuItem>
            <MenuItem
              type="button"
              role="menuitem"
              $danger
              onClick={handleDelete}
            >
              <DeleteOutlineRounded />
              Delete
            </MenuItem>
          </Menu>,
          document.body,
        )}
    </MenuWrapper>
  );
};
